import { isEmpty, isObjectLike, omit } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';

import { Defined } from 'app/@types/utils/defined';
import useStores from 'hooks/use-stores';
import { BulletinData } from 'pages/Bulletin/types/bulletin-data';
import { formatSource } from 'pages/Bulletin/utils';
import filterObject from 'utils/filter-object';
import Utils from 'utils/Utils';

export default function useBulletin(id: string) {
    const { bulletinStore } = useStores();

    const load = useCallback(
        async (id: string) => {
            await bulletinStore.loadItem({ id });

            if (bulletinStore.shortTitle) {
                document.title = bulletinStore.shortTitle;
            }
            if (bulletinStore.shortDescription) {
                document
                    .querySelector('meta[name=description]')
                    ?.setAttribute('content', bulletinStore.shortDescription);
            }
            if (typeof window.ga === 'function') {
                window.ga('set', {
                    page: window.location.pathname + window.location.search,
                    location: window.location.href,
                    title: document.title
                });
                window.ga('send', 'pageview');
            }
            Utils.scrollTo(0, 0);
        },
        [bulletinStore]
    );

    useEffect(() => void load(id), [id, load]);

    const isLoading = bulletinStore.isLoading;
    const error = bulletinStore.error;

    const formatMetrics = useCallback((metrics: object | undefined) => {
        if (!metrics) {
            return {};
        }
        const filtered = filterObject(
            metrics,
            (entry) =>
                isObjectLike(entry) &&
                !isEmpty(entry) &&
                Object.values(entry).every((value) => isObjectLike(value) && !isEmpty(value))
        );

        const grouped = Object.entries(filtered).reduce<Defined<BulletinData['metrics']>>((output, [source, items]) => {
            Object.entries(items).forEach(([key, value]) => {
                output[key] ||= [];
                output[key].push({ source: formatSource(source), value: value as never });
            });
            return output;
        }, {});

        return grouped;
    }, []);

    const data: BulletinData | undefined = useMemo(() => {
        if (bulletinStore.data.id) {
            return {
                id: bulletinStore.data.id,
                title: bulletinStore.data.title || bulletinStore.data.id,
                type: bulletinStore.data.type,
                description: {
                    short: bulletinStore.data.enchantments?.short_description ?? bulletinStore.shortDescription,
                    full: bulletinStore.data.description?.trim()
                },
                sourceData: bulletinStore.data.sourceData,
                privateArea: Boolean(bulletinStore.data.privateArea),
                detections: bulletinStore.data.cpeConfigurations
                    ? omit(bulletinStore.data.cpeConfigurations, '_draft')
                    : bulletinStore.data.cpeConfigurations,
                packages: bulletinStore.data.affectedPackage,
                affected: {
                    adp: bulletinStore.data.adpAffected,
                    cna: bulletinStore.data.cnaAffected
                },
                references: bulletinStore.data.references,
                metrics: formatMetrics(bulletinStore.data.metrics),
                aiScore: bulletinStore.data.enchantments?.score,
                epss: bulletinStore.data.epss,
                reporter: bulletinStore.data.reporter,
                href: bulletinStore.data.href,
                published: bulletinStore.data.published,
                viewCount: bulletinStore.data.viewCount,
                social: {
                    twitter: bulletinStore.data.enchantments?.twitter,
                    reddit: bulletinStore.data.enchantments?.reddit
                },
                related: bulletinStore.references,
                webApplicable: bulletinStore.data.webApplicability?.applicable,
                webVulnerabilities: bulletinStore.data.webApplicability?.vulnerabilities,
                raw: {
                    data: bulletinStore.data
                }
            };
        }
    }, [bulletinStore.data, bulletinStore.references, bulletinStore.shortDescription, formatMetrics]);

    return { isLoading, data, error };
}
